import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import { API, useAuth } from "@/App";
import { ArrowLeft, Phone, Lock, Home, Eye, EyeOff } from "lucide-react";
import useHostelSettings from "@/hooks/useHostelSettings";
import ForgotPasswordDialog from "@/components/ForgotPasswordDialog";
import { Helmet } from "react-helmet";

const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [role, setRole] = useState(searchParams.get("role") || "parent");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
 

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!phone || !password) {
      toast.error("Please enter phone number and password");
      return;
    }
    // check length of phone number
    if (phone.length !== 10) {
      toast.error("Please enter a valid phone number");
      return;
    }

    setLoading(true);
    try {
      // Use role-specific auth endpoint
      const response = await axios.post(`${API}/v1/${role}/auth/login`, {
        phone: phone,
        password: password
      });
      
      const loginData = response.data;
 
      // Store tokens and user data
      const userData = {
        ...loginData.user,
        access_token: loginData.access_token,
        refresh_token: loginData.refresh_token,
        token_type: loginData.token_type
      };
      
      login(userData);
      toast.success("Login successful!");
      
      // Redirect based on role
      if (userData.role === "admin") {
        navigate("/admin");
      } else if (userData.role === "warden") {
        navigate("/warden");
      } else {
        navigate("/parent");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "linear-gradient(to bottom, #F8FBFD 0%, #E8F4F9 100%)" }}>
     
      <Helmet>
        <title>{process.env.REACT_APP_NAME}</title>
        <link rel="icon" href="/favicon.webp" type="image/webp" />
      </Helmet>
     
      <div className="w-full max-w-md">
 
        <Card className="glass-effect animate-fade-in">
          <CardHeader className="text-center">
            {/* Hostel Logo & Name */}
            <div className="flex flex-col items-center mb-4">
             
                <img src={'/favicon.webp'} alt="Logo" className="w-20 h-20 object-contain mb-3 rounded-full" />
              
              <h1 className="text-2xl font-bold" style={{ color: "#2C5F7F" }}>
                 DormCircle
              </h1>
            </div>
            <CardTitle className="text-3xl font-bold" style={{ color: "#2C5F7F" }}>
              Welcome Back
            </CardTitle>
            <CardDescription>Sign in to access your account</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Role Selection */}
            <div className="flex gap-2 p-1 rounded-lg" style={{ background: "#E8F4F9" }}>
              <button
                data-testid="role-admin-btn"
                onClick={() => setRole("admin")}
                className={`flex-1 py-2 px-4 rounded-md font-medium transition-all ${
                  role === "admin" ? "btn-primary text-white shadow-md" : "text-gray-600"
                }`}
              >
                Admin
              </button>
              <button
                data-testid="role-warden-btn"
                onClick={() => setRole("warden")}
                className={`flex-1 py-2 px-4 rounded-md font-medium transition-all ${
                  role === "warden" ? "btn-primary text-white shadow-md" : "text-gray-600"
                }`}
              >
                Warden
              </button>
              <button
                data-testid="role-parent-btn"
                onClick={() => setRole("parent")}
                className={`flex-1 py-2 px-4 rounded-md font-medium transition-all ${
                  role === "parent" ? "btn-primary text-white shadow-md" : "text-gray-600"
                }`}
              >
                Parent
              </button>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="phone">Phone Number</Label>
                <div className="relative mt-2">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input
                    id="phone"
                    data-testid="phone-input"
                    type="tel"
                    placeholder="Enter your phone number"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="password">Password</Label>
                <div className="relative mt-2">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
                  <Input
                    id="password"
                    data-testid="password-input"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none transition-colors"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    data-testid="toggle-password-btn"
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>

              <Button
                data-testid="login-btn"
                type="submit"
                disabled={loading}
                className="w-full btn-primary text-white"
              >
                {loading ? "Logging in..." : "Login"}
              </Button>
            </form>

            {/* Reset Password Button */}
            <div className="text-center mt-4">
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-sm text-blue-600 hover:underline font-medium"
              >
                Forgot Password? Reset here
              </button>
            </div>

            {/* School Setup Link */}
            <div className="text-center mt-4 p-3 rounded-lg" style={{ backgroundColor: '#F0F9FF' }}>
              <p className="text-sm text-gray-700">
                New school?{" "}
                <button
                  type="button"
                  onClick={() => navigate("/school-setup")}
                  className="text-blue-600 hover:underline font-semibold"
                >
                  Set up here
                </button>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Forgot Password Dialog */}
      <ForgotPasswordDialog
        isOpen={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
        role={role}
      />
    </div>
  );
};

export default LoginPage;